import {toast} from "react-toastify";
import {EntityType, useGetNoteLazyQuery, useUpsertNoteMutation, VersionType} from "../../../__generated__/generated_types";
import {useMemo, useState} from "react";
import {AccountNote} from "../AccountNoteModal";
import {DriverNote} from "../../driver-note/DriverNoteModal";


export interface UseAccountNotesState {
    accountNote: AccountNote | null;
    driverNotes: DriverNote[];
    hasNote: boolean;
    isBusy: boolean;
    saveNote: (accountNote: AccountNote) => void;
    fetchNotes: (params: UseAccountNotesInputs) => void;
}

export interface UseAccountNotesInputs {
    propertyId: string | undefined;
    budgetYear: number;
    versionType: VersionType;
    accountId: string;
}

export interface UseAccountNotesProps {
    onSave?: (accountNote: AccountNote) => void;
}

export default function useAccountNotes(props?: UseAccountNotesProps): UseAccountNotesState {
    const [localAccountNote, setLocalAccountNote] = useState<AccountNote | null>(null);

    const [fetchAccountNotes, {data: accountNotesData, loading: notesLoading}] = useGetNoteLazyQuery({fetchPolicy: "network-only"});

    const [upsertNote, {loading: noteSaving}] = useUpsertNoteMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data && data.upsertNoteText) {
                props?.onSave?.({
                    accountId: data.upsertNoteText.entityId,
                    propertyId: data.upsertNoteText.propertyId,
                    budgetYear: data.upsertNoteText.year,
                    versionType: data.upsertNoteText.versionType,
                    text: data.upsertNoteText.text ?? "",
                });
                toast.success("Note updated");
            }
        },
    });

    function fetchNotes(params: UseAccountNotesInputs): void {
        if (!params.propertyId) {
            return;
        }

        setLocalAccountNote({
            accountId: params.accountId,
            propertyId: params.propertyId,
            budgetYear: params.budgetYear,
            versionType: params.versionType,
            text: null,
        });

        fetchAccountNotes({
            variables: {
                propertyId: params.propertyId,
                year: params.versionType === VersionType.Reforecast ? params.budgetYear - 1 : params.budgetYear,
                versionType: params.versionType,
                entityId: params.accountId,
                entityType: EntityType.Account,
            }
        });
    }

    const {accountNote, driverNotes} = useMemo<{accountNote: AccountNote | null, driverNotes: DriverNote[]}>(() => {
        const noteResponse = accountNotesData?.queryNote;
        const notes: {accountNote: AccountNote | null, driverNotes: DriverNote[]} = {
            accountNote: localAccountNote,
            driverNotes: [],
        }

        if (noteResponse) {
            const opNotes: DriverNote[] = noteResponse.opDriverNotes.map(n => ({...n, lineType: "operational"})) ?? [];
            const wsNotes: DriverNote[] = noteResponse.wsLineNotes.map(n => ({...n, lineType: "worksheet"})) ?? [];
            const customNotes: DriverNote[] = noteResponse.customDriverNotes.map(n => ({...n, lineType: "custom"})) ?? [];
            notes.driverNotes = [...opNotes, ...wsNotes, ...customNotes];

            notes.accountNote = {
                accountId: noteResponse.entityId,
                propertyId: noteResponse.propertyId,
                budgetYear: noteResponse.versionType === VersionType.Reforecast ? noteResponse.year + 1 : noteResponse.year,
                versionType: noteResponse.versionType,
                text: noteResponse.accountNote ?? "",
            }
        }
        return notes;
    }, [accountNotesData?.queryNote]);

    const hasNote = useMemo(() => {
        return (typeof accountNote?.text === "string" && accountNote?.text.trim() !== "") || driverNotes.length > 0;
    }, [accountNote?.text, driverNotes, notesLoading]);

    function saveNote(accountNote: AccountNote): void {
        if (!accountNote.propertyId) {
            return;
        }

        upsertNote({
            variables: {
                propertyId: accountNote.propertyId,
                year: accountNote.versionType === VersionType.Reforecast ? accountNote.budgetYear - 1 : accountNote.budgetYear,
                versionType: accountNote.versionType,
                entityId: accountNote.accountId,
                entityType: EntityType.Account,
                text: accountNote.text ?? "",
            },
        });
    }

    return {
        accountNote: accountNote,
        driverNotes: driverNotes,
        hasNote: hasNote,
        isBusy: notesLoading || noteSaving,
        fetchNotes: fetchNotes,
        saveNote: saveNote,
    };
}
